import { renderMetaTags, StructuredTextGraphQlResponse, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api, IPlanObject } from "@/api";
import {
  Layout,
  SEO,
  HeroSection,
  SelectKeyFeaturesSection,
  CustomerCareSection,
  StartFreeButtons,
  SignUpForm,
  Container,
  CredibilityBar,
  NarrativeWorkflowSection,
} from "@/components";
import {
  planComparisionFields,
  PlanComparison,
  PlanComparisonFields,
} from "@/components/PlanComparison/PlanComparison";
import {
  EditIntroSection,
  editIntroSectionFragment,
  EditIntroSectionType,
} from "@/components/edit-intro-section/EditIntroSection";
import {
  EditLaunchPromoSection,
  editLaunchPromoSectionFragment,
  EditLaunchPromoSectionType,
} from "@/components/edit-launch-promo-section/EditLaunchPromoSection";
import DealOfLifeTimeBanner, {
  DealOfLifeTimeBannerType,
  dealOfLifetimeFragment,
} from "@/components/lifetime-deal/DealOfLifeTimeBanner";
import { DATOCMS_API_TOKEN, IS_GOOGLE_AUTH_ENABLED } from "@/config";
import Fstoppers from "@/images/fstoppers.png";
import PhotoBug from "@/images/photo-bug.png";
import ShotKit from "@/images/shotkit.png";
import SlrLounge from "@/images/slr-lounge.png";
import {
  CarouselFragmentType,
  carouselFragment,
  metaTagsFragment,
  Carousel,
  responsiveImageFragment,
  AccordionFragmentType,
  Accordion,
  accordionFragment,
  statFragment,
  StatFragmentType,
  Stat,
} from "@/modules";
import { getPlansStaticProp } from "@/modules/select-plan-comparison/SelectPlanComparison";
import { SiteBanner } from "@/modules/site-banner";
import { Nullable, Subscription } from "@/types";
import { PromoBanner } from "@narrative-software/narrative-web-ui";

export const COMPANY_LOGOS = [
  { src: Fstoppers, width: 155, height: 42, companyName: "Fstoppers" },
  { src: ShotKit, width: 143, height: 41, companyName: "ShotKit" },
  { src: SlrLounge, width: 177, height: 69, companyName: "SLR Lounge" },
  { src: PhotoBug, width: 222, height: 49, companyName: "Photo Bug" },
];

interface ISelectPage {
  stats: Nullable<StatFragmentType>;
  dealBanner: DealOfLifeTimeBannerType;
  testimonials: Nullable<CarouselFragmentType>;
  faqs: Nullable<AccordionFragmentType>;
  seo: TitleMetaLinkTag[];
  planComparison: PlanComparisonFields;
  editIntroSection: Nullable<EditIntroSectionType>;
  editLaunchPromoSection: Nullable<EditLaunchPromoSectionType>;
}

interface ISelect {
  page: ISelectPage;
  banner: {
    enabled: boolean;
    content: StructuredTextGraphQlResponse;
  };
}

type SelectPageProps = {
  preview: boolean;
  subscription: Subscription<ISelect>;
  plans: IPlanObject[];
};

const SelectPage = ({ preview, subscription, plans }: SelectPageProps) => {
  const { data: { page: data, banner } = {} } = useQuerySubscription(subscription);

  if (!data) return null;

  const { stats, faqs, testimonials, planComparison, seo, dealBanner, editIntroSection, editLaunchPromoSection } = data;

  return (
    <Layout product="select" preview={preview} siteBanner={banner ? <SiteBanner {...banner} /> : null}>
      <SEO title="Narrative Select | Fast image selection | Photographers">{renderMetaTags(seo)}</SEO>
      <HeroSection
        product="select"
        title={
          <>
            Cull your photoshoot <u>twice</u> as fast
          </>
        }
        videoSrc="/videos/select-video.webm"
        videoPoster="/images/select-video-placeholder@2x.jpg"
        videoAspectRatio={1980 / 1208}
        videoLoop
      >
        Narrative is our game-changing image culling software, powered by smart tech & designed from the ground up for
        the professional photographer.
      </HeroSection>

      {stats ? <Stat {...stats} /> : null}

      {dealBanner ? <DealOfLifeTimeBanner {...dealBanner} /> : null}

      {testimonials && <Carousel {...testimonials} />}

      <PlanComparison stripePlans={plans} {...planComparison} />

      <div className="flex-col items-center py-12 px-12 bg-white">
        <Container>
          <CredibilityBar logos={COMPANY_LOGOS} label="Featured in:" />
        </Container>
      </div>

      <SelectKeyFeaturesSection title="Make quick decisions on the quality of your images" />

      {editIntroSection && <EditIntroSection {...editIntroSection} />}
      {editLaunchPromoSection && <EditLaunchPromoSection {...editLaunchPromoSection} />}

      <NarrativeWorkflowSection
        title="How Narrative fits into your image selection workflow"
        steps={[
          { title: "Import", description: "Import thousands of RAWs in 3 seconds" },
          { title: "Cull", description: "Cull your photoshoot in half the time with Narrative" },
          { title: "Edit", description: "One click import to Lightroom CC or Classic" },
        ]}
      />

      <PromoBanner
        title="Try our game changing software today"
        description={
          <>
            <span className="block">Get started with Narrative for free on macOS and Windows.</span>
            <strong className="block mt-4">Unlimited projects | Unlimited images | Integrates with Lightroom.</strong>
          </>
        }
        form={
          IS_GOOGLE_AUTH_ENABLED ? (
            <StartFreeButtons product="select" className="max-w-130" />
          ) : (
            <SignUpForm product="select" className="max-w-160" textClassName="text-white" />
          )
        }
        backgroundColour="gengar"
        backgroundImagePath={"/images/textures/purple-bg@2x.png"}
        large
      />

      <CustomerCareSection product="select" />

      {faqs && <Accordion {...faqs} />}
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<SelectPageProps> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageSelect {
          stats {
            ...statFragment
          }
          dealBanner {
          ...lifetimeDealFragment
          }
          testimonials {
            ...testimonialCarouselFragment
          }
          faqs {
            ...accordionFragment
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
          planComparison {
            ${planComparisionFields}
          }
          editIntroSection {
            ...editIntroSectionFragment
          }
          editLaunchPromoSection {
            ...editLaunchPromoSectionFragment
          }
        }
        banner: moduleSiteBanner {
          enabled
          content {
            value
          }
        }
      }
      ${statFragment}
      ${carouselFragment}
      ${accordionFragment}
      ${metaTagsFragment}
      ${responsiveImageFragment}
      ${dealOfLifetimeFragment}
      ${editIntroSectionFragment}
      ${editLaunchPromoSectionFragment}
    `,
  };

  const [{ data }, plans] = await Promise.all([
    Api.datocmsGraphqlRequest<ISelect>(graphqlRequest),
    getPlansStaticProp(),
  ]);

  if ("errors" in data) {
    console.log(data.errors, graphqlRequest.query);
    return {} as any;
  }

  const initialData = data.data;

  return {
    props: {
      preview,
      plans,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default SelectPage;
